/**
 * <collection-back-to-top>
 *
 * Collection-only floating control that appears after the visitor has scrolled
 * beyond the first viewport and starts scrolling upward.
 *
 * Public contract:
 *   - `.is-visible` controls the CSS entrance/exit state.
 *   - `[aria-hidden]` and the button tabindex mirror that visual state.
 */

import { scrollListener } from '@theme/scroll-listener';

const MIN_DIRECTION_DELTA = 2;

class CollectionBackToTop extends HTMLElement {
  #button = null;
  #lastScrollY = 0;
  #scrollRafId = null;
  #scrollCleanup = null;

  #onClick = () => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    window.scrollTo({ top: 0, left: 0, behavior });
  };

  #onScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateVisibility();
    });
  };

  connectedCallback() {
    this.#button = this.querySelector('.collection-back-to-top__button');
    if (!this.#button || this.#scrollCleanup) return;

    this.#lastScrollY = window.scrollY;
    this.#button.addEventListener('click', this.#onClick);
    this.#scrollCleanup = scrollListener.addScrollListener(this, this.#onScroll);
    this.#setVisible(false);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    this.#scrollCleanup?.removeScrollListener();

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
    }

    this.#button = null;
    this.#scrollRafId = null;
    this.#scrollCleanup = null;
  }

  #updateVisibility() {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.#lastScrollY;
    const isBeyondFirstViewport = currentScrollY > window.innerHeight;

    if (!isBeyondFirstViewport) {
      this.#setVisible(false);
    } else if (scrollDelta < -MIN_DIRECTION_DELTA) {
      this.#setVisible(true);
    } else if (scrollDelta > MIN_DIRECTION_DELTA) {
      this.#setVisible(false);
    }

    this.#lastScrollY = currentScrollY;
  }

  #setVisible(isVisible) {
    this.classList.toggle('is-visible', isVisible);
    this.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

    if (this.#button) {
      this.#button.tabIndex = isVisible ? 0 : -1;
    }
  }
}

if (!customElements.get('collection-back-to-top')) {
  customElements.define('collection-back-to-top', CollectionBackToTop);
}
